import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { ChevronRight } from "@assets";
import { Button } from "@components/Button";
import { trumpContributionFrequencyLabel } from "@components/Goals/TrumpSidecar";
import { TextInput } from "@components/Input";
import { Loader } from "@components/Loader";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { TaxFilingInfoCard, TaxGainHarvestOptions } from "@components/TaxGainHarvest";
import { BodySmall } from "@components/Typography";
import { VALID_CURRENCY_INPUT_REGEX } from "@constants";
import { GoalsContextProvider } from "@contexts";
import { AccountType, Flow, GoalType, Routes, TrumpContributionFrequency } from "@ctypes";
import {
  useEditGoal,
  useErrorBanner,
  useGoalFlow,
  useGoalsContext,
  useHarvestAccountDefaultConfigs,
  useModal,
  useNavigateToStep,
  useUpdateHarvestAccountConfigs,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import {
  convertCentsToDollar,
  convertDollarToCents,
  formatCurrency,
  formatCurrencyInput,
} from "@utils";
import { AdjustBalanceModal } from "./AdjustBalanceModal";
import { AdjustContributionModal } from "./AdjustContributionModal";
import { useEditGoalStyles } from "./styles";
import { EditGoalRouteProps, HarvestSettings } from "./types";

export const EditGoalRoute = () => {
  const location = useLocation();

  const { beneficiaryId, goalId } = (location.state as EditGoalRouteProps);

  return (
    <GoalsContextProvider beneficiaryId={beneficiaryId} goalId={goalId}>
      <EditGoalComponent />
    </GoalsContextProvider>
  );
};

const EditGoalComponent = () => {
  const styles = useEditGoalStyles();
  const { goalName, goal, goalId, beneficiaryId } = useGoalsContext();

  const { resetGoalProps } = useGoalFlow();

  const { showErrorBanner } = useErrorBanner();

  const [editGoal, { loading }] = useEditGoal();
  const [updateHarvestAccountConfigs, { loading: updatingHarvestConfigs }] =
    useUpdateHarvestAccountConfigs();

  const navigate = useNavigateToStep();
  const { show } = useModal();

  const harvestAccount = goal?.accounts?.find(
    (account) => account?.type === AccountType.HarvestUtmaUgma,
  );
  const isHarvestGoal = Boolean(harvestAccount);

  const { harvestAccountDefaultConfigs, loading: loadingHarvestDefaults } =
    useHarvestAccountDefaultConfigs({ skip: !isHarvestGoal });

  const props: MainLayoutProps = {
    pageHeader: i18n.t(Translations.EditGoalRouteHeader),
  };

  const harvestRecommendedFloorUsd =
    harvestAccount?.harvestRecommendedFloorUsd ??
    harvestAccountDefaultConfigs?.harvestRecommendedFloorUsd ??
    0;

  const targetAmount = formatCurrency(convertCentsToDollar(goal?.targetAmountInCents), false);
  const targetAmountValue = Math.round(convertCentsToDollar(goal?.targetAmountInCents));

  const harvestFloor = formatCurrency(harvestRecommendedFloorUsd, false);

  const isTrumpGoal = goal?.type === GoalType.TrumpAccountSidecar;

  const initialTrumpBalance = convertCentsToDollar(goal?.trumpAccountBalanceInCents);
  const initialTrumpContributionAmount = convertCentsToDollar(goal?.trumpContributionAmountInCents);
  const initialTrumpFrequency =
    goal?.trumpContributionFrequency ?? TrumpContributionFrequency.Weekly;

  const [trumpBalance, setTrumpBalance] = useState(initialTrumpBalance);
  const [trumpContributionAmount, setTrumpContributionAmount] = useState(
    initialTrumpContributionAmount,
  );
  const [trumpFrequency, setTrumpFrequency] = useState(initialTrumpFrequency);
  const [trumpInitials, setTrumpInitials] = useState<{
    balance: number;
    contributionAmount: number;
    frequency: TrumpContributionFrequency;
  } | null>(null);

  const [amount, setAmount] = useState(targetAmount);
  const [standardDeduction, setStandardDeduction] = useState(false);
  const [longTermCapitalGains, setLongTermCapitalGains] = useState(false);
  const [harvestInitials, setHarvestInitials] = useState<{
    standardDeduction: boolean;
    longTermCapitalGains: boolean;
  } | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect -- sync query data to local state */
  useEffect(() => {
    if (!isHarvestGoal || loadingHarvestDefaults) {
      return;
    }

    const nextStandardDeduction =
      harvestAccount?.harvestStandardDeduction ??
      harvestAccountDefaultConfigs?.harvestStandardDeduction ??
      false;
    const nextLongTermCapitalGains =
      harvestAccount?.harvestLongTermCapitalGains ??
      harvestAccountDefaultConfigs?.harvestLongTermCapitalGains ??
      false;

    setStandardDeduction(nextStandardDeduction);
    setLongTermCapitalGains(nextLongTermCapitalGains);
    setHarvestInitials({
      standardDeduction: nextStandardDeduction,
      longTermCapitalGains: nextLongTermCapitalGains,
    });
  }, [
    isHarvestGoal,
    loadingHarvestDefaults,
    harvestAccount?.harvestStandardDeduction,
    harvestAccount?.harvestLongTermCapitalGains,
    harvestAccountDefaultConfigs?.harvestStandardDeduction,
    harvestAccountDefaultConfigs?.harvestLongTermCapitalGains,
  ]);

  useEffect(() => {
    if (!isTrumpGoal) return;

    setTrumpBalance(initialTrumpBalance);
    setTrumpContributionAmount(initialTrumpContributionAmount);
    setTrumpFrequency(initialTrumpFrequency);
    setTrumpInitials({
      balance: initialTrumpBalance,
      contributionAmount: initialTrumpContributionAmount,
      frequency: initialTrumpFrequency,
    });
  }, [isTrumpGoal, initialTrumpBalance, initialTrumpContributionAmount, initialTrumpFrequency]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const amountValue = Number(amount.replace(/[$,]/g, ""));
  const invalidAmountInput = !VALID_CURRENCY_INPUT_REGEX.test(amount);

  const targetAmountChanged = amountValue !== targetAmountValue;
  const amountDisabled = invalidAmountInput || !amount.length || !targetAmountChanged;
  const harvestSettingsChanged =
    harvestInitials != null &&
    (standardDeduction !== harvestInitials.standardDeduction ||
      longTermCapitalGains !== harvestInitials.longTermCapitalGains);
  const trumpSettingsChanged =
    trumpInitials != null &&
    (trumpBalance !== trumpInitials.balance ||
      trumpContributionAmount !== trumpInitials.contributionAmount ||
      trumpFrequency !== trumpInitials.frequency);

  const handleGoalName = () => {
    if (goalId && beneficiaryId) {
      resetGoalProps({
        flow: Flow.EditGoal,
        goalId,
        beneficiaryId,
      });

      navigate(Routes.GoalType);
    } else {
      showErrorBanner();
    }
  };

  const updateHarvestSettings = async ({
    standardDeduction: nextStandardDeduction,
    longTermCapitalGains: nextLongTermCapitalGains,
  }: HarvestSettings) => {
    if (!harvestAccount?.id) {
      showErrorBanner();
      return false;
    }

    try {
      await updateHarvestAccountConfigs({
        variables: {
          accountId: harvestAccount.id,
          harvestStandardDeduction: nextStandardDeduction,
          harvestLongTermCapitalGains: nextLongTermCapitalGains,
          harvestRecommendedFloorUsd,
        },
      });

      return true;
    } catch {
      showErrorBanner();
      return false;
    }
  };

  const updateGoalAmount = async () => {
    if (goalId) {
      try {
        await editGoal({
          variables: {
            goalId,
            targetAmountInCents: convertDollarToCents(amountValue),
          },
        });

        return true;
      } catch {
        showErrorBanner();
        return false;
      }
    }

    showErrorBanner();
    return false;
  };

  const handleGoalAmount = async () => {
    const updated = await updateGoalAmount();

    if (updated) {
      navigate(-1);
    }
  };

  const handleHarvestGoal = async () => {
    if (targetAmountChanged) {
      const updatedGoalAmount = await updateGoalAmount();

      if (!updatedGoalAmount) return;
    }

    if (harvestSettingsChanged) {
      const updatedHarvestSettings = await updateHarvestSettings({
        standardDeduction,
        longTermCapitalGains,
      });

      if (!updatedHarvestSettings) return;
    }

    navigate(-1);
  };

  const handleTrumpGoal = async () => {
    if (!goalId) {
      showErrorBanner();
      return;
    }

    try {
      await editGoal({
        variables: {
          goalId,
          ...(targetAmountChanged && {
            targetAmountInCents: convertDollarToCents(amountValue),
          }),
          ...(trumpBalance !== trumpInitials?.balance && {
            trumpAccountBalanceInCents: convertDollarToCents(trumpBalance),
          }),
          ...(trumpContributionAmount !== trumpInitials?.contributionAmount && {
            trumpContributionAmountInCents: convertDollarToCents(trumpContributionAmount),
          }),
          ...(trumpFrequency !== trumpInitials?.frequency && {
            trumpContributionFrequency: trumpFrequency,
          }),
        },
      });

      navigate(-1);
    } catch {
      showErrorBanner();
    }
  };

  const ctaDisabled = (() => {
    if (isTrumpGoal) {
      return (
        invalidAmountInput || !amount.length || (!targetAmountChanged && !trumpSettingsChanged)
      );
    }

    if (isHarvestGoal) {
      return (
        invalidAmountInput || !amount.length || (!targetAmountChanged && !harvestSettingsChanged)
      );
    }

    return amountDisabled;
  })();
  const ctaLoading = isHarvestGoal ? loading || updatingHarvestConfigs : loading;
  const handlePrimaryCTA = (() => {
    if (isTrumpGoal) return handleTrumpGoal;
    if (isHarvestGoal) return handleHarvestGoal;

    return handleGoalAmount;
  })();

  const onChangeGoalAmount = (input: string) => {
    const newAmount = formatCurrencyInput({ input });
    setAmount(newAmount);
  };

  if (isHarvestGoal && loadingHarvestDefaults) {
    return (
      <MainLayout {...props}>
        <Loader />
      </MainLayout>
    );
  }

  return (
    <MainLayout {...props}>
      <Spacing size={SpacingSizes.M} />

      <div onClick={handleGoalName}>
        <TextInput
          label={i18n.t(Translations.EditGoalRouteGoalNameLabel)}
          value={goalName}
          rightIcon={<ChevronRight />}
          style={styles.input}
          readOnly
        />
      </div>

      {isTrumpGoal ? (
        <>
          <Spacing size={SpacingSizes.S} />
          <div
            onClick={() =>
              show(<AdjustBalanceModal currentBalance={trumpBalance} onUpdate={setTrumpBalance} />)
            }
          >
            <TextInput
              label={i18n.t(Translations.EditGoalRouteTrumpBalanceInputLabel)}
              value={formatCurrency(trumpBalance)}
              rightIcon={<ChevronRight />}
              style={styles.input}
              readOnly
            />
          </div>
          <Spacing size={SpacingSizes.S} />
          <div
            onClick={() =>
              show(
                <AdjustContributionModal
                  currentAmount={trumpContributionAmount}
                  currentFrequency={trumpFrequency}
                  onUpdate={(nextAmount, nextFrequency) => {
                    setTrumpContributionAmount(nextAmount);
                    setTrumpFrequency(nextFrequency);
                  }}
                />,
              )
            }
          >
            <TextInput
              label={i18n.t(Translations.EditGoalRouteTrumpContributionInputLabel)}
              value={`${formatCurrency(trumpContributionAmount)} ${trumpContributionFrequencyLabel[trumpFrequency]}`}
              rightIcon={<ChevronRight />}
              style={styles.input}
              readOnly
            />
          </div>
        </>
      ) : null}

      {isHarvestGoal ? (
        <>
          <Spacing size={SpacingSizes.S} />
          <BodySmall>{i18n.t(Translations.EditGoalRouteHarvestDescription)}</BodySmall>
          <Spacing size={SpacingSizes.M} />
          <TaxGainHarvestOptions
            standardDeduction={standardDeduction}
            longTermCapitalGains={longTermCapitalGains}
            onStandardDeductionChange={setStandardDeduction}
            onLongTermCapitalGainsChange={setLongTermCapitalGains}
            disabled={ctaLoading}
          />
          <Spacing size={SpacingSizes.M} />
          <div style={styles.harvestFloorCard}>
            <BodySmall>{i18n.t(Translations.EditGoalRouteHarvestFloorInputLabel)}</BodySmall>
            <Spacing size={SpacingSizes.XXS} />
            <div style={styles.harvestFloorValue}>{harvestFloor}</div>
            <Spacing size={SpacingSizes.XS} />
            <BodySmall style={styles.harvestFloorHint}>
              {i18n.t(Translations.EditGoalRouteHarvestFloorDescription)}
            </BodySmall>
          </div>
          <Spacing size={SpacingSizes.S} />
          <TaxFilingInfoCard />
          <Spacing size={SpacingSizes.S} />
          <TextInput
            label={i18n.t(Translations.EditGoalRouteGoalAmountLabel)}
            value={amount}
            onChangeText={onChangeGoalAmount}
            style={styles.input}
          />
          <Spacing size={SpacingSizes.L} />
        </>
      ) : (
        <>
          <Spacing size={SpacingSizes.XS} />
          <TextInput
            label={i18n.t(Translations.EditGoalRouteGoalAmountLabel)}
            value={amount}
            onChangeText={onChangeGoalAmount}
            style={styles.input}
          />
        </>
      )}

      <Spacing size={SpacingSizes.M} />

      <Button
        title={i18n.t(Translations.EditGoalRoutePrimaryCTA)}
        onClick={handlePrimaryCTA}
        loading={ctaLoading}
        disabled={ctaDisabled}
      />
    </MainLayout>
  );
};
